import { DEFAULT_ENTITY_MAP_FILTER_STATE, type EntityMapFilterState } from './entityMapFilters';
import { LEAK_ENTITY_STATUSES, POI_ENTITY_STATUSES } from './entityStatus';

// Split-model committed-filters persistence. Follows the pre-split module's conventions:
// the key embeds the version (so this lives beside the untouched v1 record — never deleted
// here; the post-release cleanup removes it), a version field guards the payload, loads
// hard-reject to null, and the state validator repairs field-by-field so a stale payload
// from an older build never nulls the whole record.

export const ENTITY_MAP_FILTERS_VERSION = 2;
export const ENTITY_MAP_FILTERS_KEY = `orbit.map.filters.v${String(ENTITY_MAP_FILTERS_VERSION)}`;

export interface PersistedEntityMapFilters {
    isActive: boolean;
    committed: EntityMapFilterState;
}

function repairStatusToggles<S extends string>(
    registry: readonly { status: S; enabled: boolean }[],
    stored: unknown,
): Record<S, boolean> {
    const result = Object.fromEntries(registry.map((d) => [d.status, d.enabled])) as Record<S, boolean>;
    if (typeof stored !== 'object' || stored === null) return result;
    for (const def of registry) {
        const value = (stored as Record<string, unknown>)[def.status];
        // Disabled statuses render nowhere — a stored true can never resurrect one.
        if (typeof value === 'boolean' && def.enabled) result[def.status] = value;
    }
    return result;
}

/**
 * Repairs an arbitrary stored/URL payload into a valid EntityMapFilterState: every field
 * is validated independently against the defaults, so unknown fields are dropped, missing
 * fields keep their defaults, and off-vocabulary values are discarded.
 */
export function validateEntityMapFilterState(raw: unknown): EntityMapFilterState {
    const defaults = DEFAULT_ENTITY_MAP_FILTER_STATE;
    if (typeof raw !== 'object' || raw === null) {
        return {
            ...defaults,
            poiStatuses: { ...defaults.poiStatuses },
            leakStatuses: { ...defaults.leakStatuses },
            pipeTypes: { ...defaults.pipeTypes },
        };
    }
    const stored = raw as Record<string, unknown>;

    const show = stored['show'];
    const dateRange = stored['dateRange'];
    const leakVisibility = stored['leakVisibility'];

    const customStored =
        typeof stored['customDateRange'] === 'object' && stored['customDateRange'] !== null
            ? (stored['customDateRange'] as Record<string, unknown>)
            : {};
    const from = typeof customStored['from'] === 'string' ? customStored['from'] : null;
    const to = typeof customStored['to'] === 'string' ? customStored['to'] : null;

    const pipeStored =
        typeof stored['pipeTypes'] === 'object' && stored['pipeTypes'] !== null
            ? (stored['pipeTypes'] as Record<string, unknown>)
            : {};
    const pipeTypes = Object.fromEntries(
        (Object.keys(defaults.pipeTypes) as (keyof EntityMapFilterState['pipeTypes'])[]).map((key) => [
            key,
            typeof pipeStored[key] === 'boolean' ? pipeStored[key] : defaults.pipeTypes[key],
        ]),
    ) as EntityMapFilterState['pipeTypes'];

    return {
        show: show === 'all' || show === 'searched' || show === 'created' ? show : defaults.show,
        dateRange:
            dateRange === 'all' ||
            dateRange === 'thisMonth' ||
            dateRange === 'thisQuarter' ||
            dateRange === 'thisYear' ||
            dateRange === 'custom'
                ? dateRange
                : defaults.dateRange,
        customDateRange: { from, to },
        leakVisibility:
            leakVisibility === 'all' || leakVisibility === 'visible' || leakVisibility === 'notVisible'
                ? leakVisibility
                : defaults.leakVisibility,
        poiStatuses: repairStatusToggles(POI_ENTITY_STATUSES, stored['poiStatuses']),
        leakStatuses: repairStatusToggles(LEAK_ENTITY_STATUSES, stored['leakStatuses']),
        pipeTypes,
    };
}

/**
 * Loads the persisted split-model filters, or null when nothing valid/active is stored
 * (inactive states are not persisted — clearing writes removal, matching the pre-split
 * convention where null simply means "filters inactive, everything visible").
 */
export function loadPersistedEntityMapFilters(): PersistedEntityMapFilters | null {
    try {
        const raw = localStorage.getItem(ENTITY_MAP_FILTERS_KEY);
        if (!raw) return null;
        const parsed: unknown = JSON.parse(raw);
        if (typeof parsed !== 'object' || parsed === null) return null;
        const record = parsed as Record<string, unknown>;
        if (record['version'] !== ENTITY_MAP_FILTERS_VERSION) return null;
        if (record['isActive'] !== true) return null;
        return { isActive: true, committed: validateEntityMapFilterState(record['committed']) };
    } catch {
        return null;
    }
}

export function savePersistedEntityMapFilters(state: PersistedEntityMapFilters): void {
    try {
        localStorage.setItem(ENTITY_MAP_FILTERS_KEY, JSON.stringify({ version: ENTITY_MAP_FILTERS_VERSION, ...state }));
    } catch {
        // localStorage may be unavailable (private browsing, storage quota, etc.)
    }
}

export function clearPersistedEntityMapFilters(): void {
    try {
        localStorage.removeItem(ENTITY_MAP_FILTERS_KEY);
    } catch {
        // localStorage may be unavailable (private browsing, storage quota, etc.)
    }
}
